<template>
  <b-card v-if="users !== null">
    <b-card-header class="p-0 mb-1">
      <div class="d-flex align-items-center card-title-responsive-60">
        <h4 class="card-title">
          {{ $t('settings.users') }}
        </h4>
      </div>
      <b-button-group class="ml-1">
        <b-button
          v-if="item === null"
          id="btn-new-user"
          variant="outline-primary"
          class="btn-icon"
          @click="addNew()"
        >
          <feather-icon icon="FileIcon" />
          <b-tooltip
            triggers="hover"
            target="btn-new-user"
            :title=" $t('settings.new-user')"
          />
        </b-button>
        <b-button
          v-if="item === null"
          variant="outline-primary"
          class="btn-icon"
          @click="load()"
        >
          <feather-icon icon="RefreshCcwIcon" />
        </b-button>

        <b-button
          v-if="item !== null"
          id="btn-save-user"
          variant="outline-primary"
          class="btn-icon"
          :disabled="!somethingChanged || !isValidate"
          @click="submit()"
        >
          <feather-icon icon="SaveIcon" />
          <b-tooltip
            triggers="hover"
            target="btn-save-user"
            :title=" $t('common.save')"
          />
        </b-button>
        <b-button
          v-if="item !== null"
          id="btn-cancel-user"
          variant="outline-primary"
          class="btn-icon"
          @click="close()"
        >
          <feather-icon icon="XIcon" />
          <b-tooltip
            triggers="hover"
            target="btn-cancel-user"
            :title=" $t('common.close')"
          />
        </b-button>
      </b-button-group>
    </b-card-header>
    <b-card-body class="p-0">
      <b-table
        v-if="item === null"
        :items="users"
        :fields="tableColumns"
        show-empty
        :empty-text=" $t('common.no-matching')"
        responsive
        striped
        bordered
      >
        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-link
            class="text-nowrap ml-1"
            @click="edit(data.item)"
          >
            <feather-icon
              icon="EditIcon"
              class="cursor-pointer"
              size="16"
            />
          </b-link>
        </template>

        <template #cell(isEnabled)="data">
          <b-form-checkbox
            :checked="data.value"
            disabled
          />
        </template>
      </b-table>
      <b-form v-if="item !== null">
        <b-row>
          <b-col
            cols="12"
            lg="6"
          >
            <b-form-group
              :label="$t('settings.user-username')"
              label-for="user-username"
            >
              <b-form-input
                id="user-username"
                v-model="item.username"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            lg="6"
          >
            <b-form-group
              :label="$t('settings.user-fullname')"
              label-for="user-fullname"
            >
              <b-form-input
                id="user-fullname"
                v-model="item.fullname"
                trim
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('settings.user-areas')"
              label-for="user-areas"
            >
              <v-select
                v-model="item.settings.allowedAreas"
                :options="areas"
                multiple
                :reduce="val => val.aid"
                label="areaName"
                :placeholder="$t('common.select-one')"
                input-id="user-areas"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('settings.user-roles')"
              label-for="user-roles"
            >
              <b-form-checkbox-group
                id="user-roles"
                v-model="item.settings.roles"
                :reduce="val => val.value"
                :options="rolesOptions"
                class="demo-inline-spacing"
              />

            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
            class="mt-2"
          >
            <b-form-checkbox
              id="only-donations"
              v-model="item.isEnabled"
            >
              {{ $t('common.enabled') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-form>
    </b-card-body>
  </b-card>
</template>

<script>
import vSelect from 'vue-select'
import {
  BRow, BCol, BFormCheckbox, BFormCheckboxGroup,
  BCard, BCardBody, BCardHeader, BTable, BLink, BButton, BButtonGroup, BTooltip, BForm, BFormInput, BFormGroup,
} from 'bootstrap-vue'
import lookupsService from '@/vue.lookups.service'
import axios from '@axios'
import i18n from '@/libs/i18n'
import {
  formatDateHumanFriendly, clone, toastSuccessOptions, swalSaveOptions,
} from '@/views/layouts/layouts.utils'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTable,
    BLink,
    BCardHeader,
    BButton,
    BButtonGroup,
    BTooltip,
    BForm,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormInput,
    BFormGroup,
    vSelect,
  },
  data() {
    return {
      users: [],
      areas: [],
      item: null,
      original: null,
      somethingChanged: false,
      isValidate: false,
      rolesOptions: [],
      tableColumns: [
        { key: 'fullname', label: i18n.t('settings.user-fullname') },
        { key: 'username', label: i18n.t('settings.user-username') },
        {
          key: 'settings.allowedAreas',
          label: i18n.t('settings.user-areas'),
          tdClass: 'small',
          formatter: value => (value && this.areas.length > 0 ? value.map(a => (this.areas.find(b => b.aid === a) ? this.areas.find(b => b.aid === a).areaName : a)).join(' / ') : '-'),
        },
        {
          key: 'settings.roles',
          label: i18n.t('settings.user-roles'),
          tdClass: 'small',
          formatter: value => (value && value.length > 0 ? value.map(a => a.toUpperCase()).join(' / ') : '-'),
        },
        { key: 'lastLogin', label: i18n.t('common.last-login'), formatter: formatDateHumanFriendly },
        { key: 'isEnabled', label: i18n.t('common.enabled') },
        { key: 'actions', label: i18n.t('common.actions') }],
    }
  },
  async mounted() {
    this.load()
    this.$watch('item', () => {
      if (this.item) {
        this.somethingChanged = true
        this.isValidate = (this.item.fullname && this.item.username && this.item.settings.allowedAreas.length > 0)
      }
    }, { deep: true, immediate: true })
  },
  methods: {
    async load() {
      this.users = await lookupsService.getUsers()
      this.areas = await lookupsService.getAreas()
      this.rolesOptions = lookupsService.getRolesOptions()
    },
    reset(deep) {
      if (deep && this.original) {
        Object.assign(this.item, this.original)
      }
      this.item = null
      this.original = null
    },
    addNew() {
      this.item = {
        uid: null,
        fullname: null,
        username: null,
        isEnabled: true,
        settings: { allowedAreas: [], roles: [] },
      }
      if (this.areas && this.area.length === 1) {
        this.item.settings.allowedAreas = [this.areas[0]]
      }
      this.original = null
      setTimeout(() => { this.somethingChanged = false }, 250)
    },
    edit(item) {
      this.item = item
      this.original = clone(item)
      setTimeout(() => { this.somethingChanged = false }, 250)
    },
    close() {
      if (this.original !== null && this.somethingChanged) {
        this.$swal(swalSaveOptions).then(result => {
          if (result.value === true) {
            this.submit()
          } else if (result.value === false) {
            this.reset(true)
          }
        })
      } else { this.reset(true) }
    },
    async submit() {
      const uid = (this.original === null ? 0 : this.item.uid)
      const response = await axios.post('/api/auth/users', JSON.stringify(this.item), { params: { uid } })
      if (uid === 0) { this.item.uid = response.data.uid }
      if (this.original === null) { // new item
        this.users.push(this.item)
      }
      this.reset(false)
      this.$toast(toastSuccessOptions, { position: 'top-center' })
    },
  },
  setup() {
    return {
      formatDateHumanFriendly,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.demo-inline-spacing .custom-checkbox {
    margin-top: 7px;
}
.demo-inline-spacing .custom-control-label {
  text-transform: uppercase;
}

</style>
